import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { ProposalMeetingRequestDto } from '@lib/dto/proposal-meeting.dto'
import type { RequestMeetingInput } from '@lib/input/proposal-meeting.input'
import { proposalService } from '@/services/ProposalService'
import { proposalKeys } from '@/hooks/queryKeys'

/**
 * Board meetings a client asked someone to attend, for one version.
 *
 * Requests arrive from the shared page at any moment, with nothing in this
 * app to invalidate against, so the list is simply refetched whenever the
 * detail screen comes back into focus rather than cached for a while.
 */
export function useMeetingRequests(
  versionId: string,
): UseQueryResult<ProposalMeetingRequestDto[]> {
  return useQuery({
    queryKey: proposalKeys.meetingRequests(versionId),
    queryFn: () => proposalService.listMeetingRequests(versionId),
    enabled: versionId !== '',
  })
}

/**
 * Ask for someone at the board meeting, from the shared proposal.
 *
 * No cache to update: the person asking has no account and cannot see the
 * list. The returned row is what the page confirms back to them.
 */
export function useRequestMeeting(
  token: string,
): UseMutationResult<ProposalMeetingRequestDto, Error, RequestMeetingInput> {
  return useMutation({
    mutationFn: (input: RequestMeetingInput) =>
      proposalService.requestMeetingShared(token, input),
  })
}
